import { useEffect, useState } from 'react';
import { syncLearningData, syncAudio } from '../engines/offline_pipeline';
import { checkForUpdate, isDictationActive, onDictationEnd } from '../pwaUpdate';

// Co ile synchronizować dane samouczenia (mapowania słownikowe + log korekt)
// przy stałym połączeniu, niezależnie od zdarzenia 'online'. Skrócone z 10 do 2
// minut — dodatkowe zabezpieczenie na wypadek, gdyby ani 'online', ani
// 'visibilitychange' się nie odpaliły.
const LEARNING_SYNC_INTERVAL_MS = 2 * 60 * 1000; // 2 minuty

// Szybki, aktywny "watchdog" trybu samolotowego — zdarzenie 'online' w praktyce
// (zwłaszcza po przełączeniu trybu samolotowego, a nie samego Wi-Fi) bywa
// odpalane z zauważalnym opóźnieniem albo wcale, jeśli karta jest w tle. Na
// potrzeby demo synchronizacja MUSI ruszyć w ciągu sekund od przywrócenia
// połączenia, nie kiedyś w ciągu następnych minut — dlatego dodatkowo pilnujemy
// zmiany navigator.onLine cyklicznie, niezależnie od zdarzeń przeglądarki.
const AIRPLANE_MODE_WATCH_MS = 1500;

export function useNetworkSync(): void {
  useEffect(() => {
    // Synchronizacja nigdy nie startuje W TRAKCIE aktywnego dyktowania — mikrofon
    // i sieć nie mają rywalizować o zasoby na słabszych telefonach. Bezpieczniejsze
    // niż przerywanie już trwającego nagrywania (SpeechRecognition na iOS wymaga
    // startu synchronicznie w geście dotyku — przerwanie/wstrzymanie w połowie
    // groziłoby zepsuciem dyktowania). Gdy dyktowanie się skończy, sync odpala się
    // natychmiast (patrz onDictationEnd), więc efekt jest równoważny bez tego ryzyka.
    const runSync = () => {
      if (isDictationActive()) return;
      // UWAGA: notatki NIE są już wypychane starym `sync()` z offline_pipeline —
      // ten tor dublował wysyłkę tej samej notatki, którą kolejkuje nowy
      // `SyncQueueRepository` (`note/create` → POST /api/sync). Kanoniczną
      // synchronizację notatek prowadzi teraz `OfflineSyncService`
      // (NetworkSyncProvider). Tu zostają wyłącznie tory bez odpowiednika w
      // nowej warstwie: samouczenie słownika i upload zaległego audio.
      syncLearningData().catch(() => {});
      syncAudio().catch(() => {});
    };

    // Powrót połączenia: synchronizacja notatek/uczenia + sprawdzenie nowej wersji PWA.
    const handleOnline = () => {
      runSync();
      checkForUpdate();
    };

    // Zdarzenie 'online' bywa niemiarodajne w mobilnych przeglądarkach/PWA
    // (zwłaszcza po przełączeniu trybu samolotowego, a nie samego Wi-Fi, i gdy
    // karta była w tle) — dlatego dodatkowo synchronizujemy przy KAŻDYM powrocie
    // aplikacji na pierwszy plan, jeśli w tym momencie faktycznie jest połączenie.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        runSync();
      }
    };

    if (navigator.onLine) runSync();
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    onDictationEnd(() => { if (navigator.onLine) runSync(); });

    const interval = setInterval(() => {
      if (navigator.onLine) runSync();
    }, LEARNING_SYNC_INTERVAL_MS);

    // Watchdog trybu samolotowego — wykrywa przejście offline→online samodzielnie,
    // bez polegania na tym, czy przeglądarka w ogóle odpali zdarzenie 'online'.
    let wasOnline = navigator.onLine;
    const airplaneWatch = setInterval(() => {
      const nowOnline = navigator.onLine;
      if (nowOnline && !wasOnline) {
        runSync();
        checkForUpdate();
      }
      wasOnline = nowOnline;
    }, AIRPLANE_MODE_WATCH_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
      clearInterval(airplaneWatch);
    };
  }, []);
}

export function useOnline(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  return isOnline;
}
